import {useCallback, useEffect, useMemo, useState} from 'react';

import {getAvailableAccessRights} from 'src/admin-rights-service/components/permission-tree/utils/getAvailableAccessRights';
import {assignAdminProfile} from 'src/openapi/admin/apis/AssignAdminProfileApi';
import {getAdminRightsById} from 'src/openapi/admin/apis/GetAdminRightsByIdApi';
import {unassignAdminProfile} from 'src/openapi/admin/apis/UnassignAdminProfileApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    userId: number,
};

export const useAdminPermissions = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const [isLoading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const [initialAdminPermissions, setInitialAdminPermissions] = useState<string[]>([]);
    const [adminPermissions, setAdminPermissions] = useState<string[]>([]);

    useEffect(() => {
        const loadAdminPermissions = async () => {
            setLoading(true);
            const adminPermissionsResponse = await apiDispatch(getAdminRightsById({adminId: userId}));
            if (adminPermissionsResponse.payload.success) {
                const availablePermissions = getAvailableAccessRights(adminPermissionsResponse.payload.data);
                setInitialAdminPermissions(availablePermissions);
                setAdminPermissions(availablePermissions);
            }

            if (adminPermissionsResponse.payload.error) {
                setLoadingError(adminPermissionsResponse.payload.data);
            }

            setLoading(false);
        };

        loadAdminPermissions();
    }, [userId, apiDispatch]);

    const saveAdminPermissions = useCallback(async (
        roleId?: number, initialRoleId?: number, options?: SaveOptions,
    ) => {
        let error = '';

        if (roleId) {
            if (!adminPermissions.length) {
                return 'At least one permission must be selected';
            }

            const saveAdminPermissionsResponse = await apiDispatch(assignAdminProfile({
                adminId: userId,
                profileId: roleId,
                assignAdminProfileRequest: {permissions: adminPermissions, reason: options?.reason},
            }));
            if (saveAdminPermissionsResponse.payload.error) {
                error = getError({error: saveAdminPermissionsResponse.payload.data, separator: ' '});
            } else {
                setInitialAdminPermissions(adminPermissions);
            }
        } else if (initialRoleId) {
            const removeAdminPermissionsResponse = await apiDispatch(unassignAdminProfile({
                reason: options?.reason, adminId: userId,
            }));
            if (removeAdminPermissionsResponse.payload.error) {
                error = getError({error: removeAdminPermissionsResponse.payload.data, separator: ' '});
            } else {
                setInitialAdminPermissions([]);
            }
        }

        return error;
    }, [adminPermissions, userId, apiDispatch]);

    const discardAdminPermissions = useCallback(() => {
        setAdminPermissions(initialAdminPermissions);
    }, [initialAdminPermissions]);

    const isInitialAdminPermissions = useMemo(
        () => isDeepEqual({prev: initialAdminPermissions, current: adminPermissions}),
        [initialAdminPermissions, adminPermissions],
    );

    return {
        isLoading,
        loadingError,
        adminPermissions,
        isInitialAdminPermissions,
        setAdminPermissions,
        saveAdminPermissions,
        discardAdminPermissions,
    };
};
